import store from '@/store';
import { dasherize } from 'utils/Helpers';

/** Default exchange entry when we cannot build a schedule / ticket-list URL. */
export const EXCHANGE_HOME_PATH = '/exchange';

/** Builds `/exchange/schedule|ticket-list/...` paths from segments (reads ticket-list flag from store). */
export function createSchedulePath(operationNumbers, NIT, origin, destination, email, document) {
  const { whitelabelConfig } = store.getState();

  let url = `/exchange/${
    whitelabelConfig.features.USE_EXCHANGE_TICKET_LIST_ROUTE ? 'ticket-list' : 'schedule'
  }`;
  if (operationNumbers) url += `/${operationNumbers}`;
  if (NIT) url += `/${NIT}`;
  if (origin) url += `/${origin}`;
  if (destination) url += `/${destination}`;
  if (email) url += `/${email}`;
  if (document) url += `/${document}`;
  return url;
}

/**
 * Build the exchange schedule URL path with proper origin/destination from the selected trip.
 * @param {string} operationNumbers - The operation numbers for the exchange
 * @param {object} trip - The selected trip object (depart or return)
 * @param {string} fallbackOrigin - Fallback origin from URL params
 * @param {string} fallbackDestination - Fallback destination from URL params
 * @returns {string} The formatted URL path for the exchange schedule
 */
export function buildExchangeSchedulePath(
  operationNumbers,
  trip,
  fallbackOrigin,
  fallbackDestination,
) {
  const tripOrigin = trip?.originDisplay ? dasherize(trip.originDisplay) : fallbackOrigin;
  const tripDestination = trip?.destinationDisplay
    ? dasherize(trip.destinationDisplay)
    : fallbackDestination;

  return `/exchange/schedule/${operationNumbers}/${tripOrigin}/${tripDestination}`;
}

/**
 * Single path segment for /exchange/schedule/:op/:origin/:destination (e.g. lima, ica).
 * @param {string} [slug] - API slug (e.g. origin_slug)
 * @param {string} [display] - Human label; dasherized if slug missing
 * @param {string|number} [fallbackId] - Often cityId from purchase fragments
 * @returns {string}
 */
function exchangeSchedulePlaceSegment(slug, display, fallbackId) {
  if (slug) return String(slug).toLowerCase();
  if (display) return dasherize(display);
  if (fallbackId != null && `${fallbackId}`.trim() !== '') return String(fallbackId).toLowerCase();
  return '';
}

/**
 * Redirect path after an exchange seat-lock timeout / window error, using Redux `exchange` + `purchase`.
 * Always uses **schedule** (`/exchange/schedule/:op/:origin/:destination`), e.g. `/exchange/schedule/EE8FAA/lima/ica`,
 * never the `ticket-list` path — even when `USE_EXCHANGE_TICKET_LIST_ROUTE` is on (that route is for other flows).
 * Falls back to {@link EXCHANGE_HOME_PATH} if operation or place segments are missing.
 * @returns {string}
 */
export function getExchangeLockScheduleRedirectPath() {
  const { exchange } = store.getState();

  const fromOperationNumbers =
    exchange.operationNumbers?.length > 0 ? exchange.operationNumbers : null;
  const fromPassengers = (exchange.passengers || []).map((p) => p.transporterKey).filter(Boolean);
  const nums =
    fromOperationNumbers ||
    (fromPassengers.length > 0 ? fromPassengers : null) ||
    (exchange.ticket?.operationNumber ? [exchange.ticket.operationNumber] : []);

  const routeOp = exchange.routeOperationSegment?.trim();
  const opSegment = routeOp || (nums.length ? nums.join(',') : '');
  if (!opSegment) return EXCHANGE_HOME_PATH;

  const { trip, ticket } = exchange;

  const origin = ticket?.origin
    ? dasherize(ticket.origin)
    : exchangeSchedulePlaceSegment(trip?.originSlug, trip?.originDisplay, null);
  const destination = ticket?.destination
    ? dasherize(ticket.destination)
    : exchangeSchedulePlaceSegment(trip?.destinationSlug, trip?.destinationDisplay, null);

  if (!origin || !destination) return EXCHANGE_HOME_PATH;

  return `/exchange/schedule/${opSegment}/${origin}/${destination}`;
}
